import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, FolderKanban, CheckSquare, Calendar, Trash2,
  Loader2, ChevronRight, Target, Clock, ArrowLeft,
  Circle, Pause, CheckCircle2, MoreHorizontal, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
import NotificationBell from "@/components/shared/NotificationBell";  // FaseC C1
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import ProjectDetailPanel from "./ProjectDetailPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Security audit (FIN-2 T1, 2026-04-30) — sections legacy.
 *
 * Backend já blindado pela SEC-1. Sem JWT, fetch retorna 401 e UI quebra.
 *
 * | Endpoint chamado                  | authFetch usado? | Notas       |
 * |-----------------------------------|------------------|-------------|
 * | GET  /api/projects                | ❌ → ✅ (T2)     | listar      |
 * | POST /api/projects                | ❌ → ✅ (T2)     | criar       |
 * | GET  /api/projects/:id            | ❌ → ✅ (T2)     | detalhe     |
 * | DEL  /api/projects/:id            | ❌ → ✅ (T2)     | deletar     |
 * | POST /api/projects/:id/tasks      | ❌ → ✅ (T2)     | criar task  |
 * | PUT  /api/projects/tasks/:id      | ❌ → ✅ (T2)     | editar task |
 * | DEL  /api/projects/tasks/:id      | ❌ → ✅ (T2)     | del task    |
 */

const stageConfig = {
  todo: { label: "A fazer", color: "#64748b", icon: Circle },
  doing: { label: "Fazendo", color: "#06b6d4", icon: Clock },
  paused: { label: "Pausado", color: "#f59e0b", icon: Pause },
  done: { label: "Feito", color: "#22c55e", icon: CheckCircle2 },
};

function StatsCard({ icon: Icon, value, label, color, subtitle }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0d1220] border border-white/[0.06] rounded-2xl p-4 hover:border-white/10 transition-all">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-white tabular-nums">{value}</h3>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
      {subtitle && <p className="text-[11px] text-slate-600 mt-2 pl-[52px]">{subtitle}</p>}
    </motion.div>
  );
}

function ProjectCard({ project, onDelete, onClick, onView360 }) {
  const totalTasks = project.task_count || project.tasks?.length || 0;
  const doneTasks = project.done_count || project.tasks?.filter(t => t.stage === "done").length || 0;
  const progress = totalTasks > 0 ? (doneTasks / totalTasks) * 100 : 0;
  const isOverdue = project.end_date && new Date(project.end_date) < new Date() && progress < 100;
  const statusColor = progress === 100 ? "#22c55e" : progress > 50 ? "#06b6d4" : progress > 0 ? "#f59e0b" : "#64748b";

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) : null;

  const stageBreakdown = useMemo(() => {
    if (!project.tasks?.length) return [];
    const counts = {};
    project.tasks.forEach(t => { const s = t.stage || "todo"; counts[s] = (counts[s] || 0) + 1; });
    return Object.entries(counts).map(([stage, count]) => ({
      name: stageConfig[stage]?.label || stage,
      value: count,
      color: stageConfig[stage]?.color || "#64748b",
    }));
  }, [project.tasks]);

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      className="bg-[#0d1220] border border-white/[0.06] rounded-2xl p-5 hover:border-white/[0.1] transition-all cursor-pointer group"
      onClick={() => onClick?.(project)} data-testid={`project-card-${project.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${statusColor}10` }}>
            <FolderKanban className="w-5 h-5" style={{ color: statusColor }} />
          </div>
          <div className="min-w-0">
            <h4 className="text-white font-medium text-sm truncate">{project.title || project.name}</h4>
            {project.description && <p className="text-xs text-slate-600 mt-0.5 line-clamp-1">{project.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onView360?.(project); }}
            className="p-1.5 rounded-lg text-slate-700 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
            title="Vista 360° (tarefas, notas, pesquisas, transações)"
            data-testid={`view360-project-${project.id}`}>
            <Sparkles className="w-3.5 h-3.5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(project); }}
            className="p-1.5 rounded-lg text-slate-700 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid={`delete-project-${project.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-slate-500"><CheckSquare className="w-3 h-3 inline mr-1" />{doneTasks}/{totalTasks} tarefas</span>
          <span className="font-semibold" style={{ color: statusColor }}>{Math.round(progress)}%</span>
        </div>
        <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="h-full rounded-full" style={{ backgroundColor: statusColor }} />
        </div>
      </div>

      {/* Stage breakdown mini */}
      {stageBreakdown.length > 0 && (
        <div className="flex gap-3 mb-3">
          {stageBreakdown.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[11px]">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="text-slate-500">{s.name}: {s.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-white/[0.04]">
        <div className="flex items-center gap-2">
          {(project.end_date || project.deadline) && (
            <span className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md ${isOverdue ? "bg-red-500/10 text-red-400" : "bg-white/[0.03] text-slate-500"}`}>
              <Calendar className="w-3 h-3" /> {formatDate(project.end_date || project.deadline)}
            </span>
          )}
          <span className={`text-[11px] px-2 py-0.5 rounded-md font-medium`} style={{ backgroundColor: `${statusColor}10`, color: statusColor }}>
            {progress === 100 ? "Completo" : project.status || "Em progresso"}
          </span>
        </div>
        <span className="flex items-center gap-1 text-[11px] text-cyan-400 font-medium group-hover:translate-x-0.5 transition-transform">
          Detalhes <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </motion.div>
  );
}

function ProjectDetail({ project, onBack, onRefresh }) {
  const [tasks, setTasks] = useState(project.tasks || []);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    setIsAdding(true);
    try {
      const res = await authFetch(`${API_URL}/api/projects/${project.id}/tasks`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTaskTitle, stage: "todo" }),
      });
      if (res.ok) {
        const t = await res.json();
        setTasks(prev => [...prev, t]);
        setNewTaskTitle("");
        toast.success("Tarefa adicionada!");
        onRefresh?.();
      }
    } catch (err) { toast.error("Erro"); }
    finally { setIsAdding(false); }
  };

  const handleStageChange = async (task, newStage) => {
    try {
      const res = await authFetch(`${API_URL}/api/projects/tasks/${task.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: newStage }),
      });
      if (res.ok) {
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, stage: newStage } : t));
        onRefresh?.();
      }
    } catch (err) { console.error(err); }
  };

  const handleDeleteTask = async (task) => {
    try {
      const res = await authFetch(`${API_URL}/api/projects/tasks/${task.id}`, { method: "DELETE" });
      if (res.ok) { setTasks(prev => prev.filter(t => t.id !== task.id)); toast.success("Removida"); onRefresh?.(); }
    } catch (err) { console.error(err); }
  };

  const stages = ["todo", "doing", "paused", "done"];
  const nextStage = (s) => stages[(stages.indexOf(s) + 1) % stages.length];

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="project-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-4 transition-colors" data-testid="project-back-btn">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h2 className="text-2xl font-bold text-white mb-1">{project.title || project.name}</h2>
      {project.description && <p className="text-sm text-slate-500 mb-6">{project.description}</p>}

      {/* Add task */}
      <div className="flex gap-2 mb-6">
        <input type="text" value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)}
          placeholder="Nova tarefa do projeto..."
          onKeyDown={(e) => e.key === "Enter" && handleAddTask()}
          className="flex-1 px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 text-sm transition-colors"
          data-testid="project-add-task-input" />
        <button onClick={handleAddTask} disabled={!newTaskTitle.trim() || isAdding}
          className="px-4 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 disabled:opacity-40 transition-all text-sm font-medium"
          data-testid="project-add-task-btn">
          {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>

      {/* Kanban columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="project-kanban">
        {stages.map(stage => {
          const conf = stageConfig[stage];
          const stageTasks = tasks.filter(t => (t.stage || "todo") === stage);
          return (
            <div key={stage} className="bg-[#0d1220] border border-white/[0.06] rounded-2xl p-3 min-h-[200px]">
              <div className="flex items-center gap-2 mb-3 px-1">
                <conf.icon className="w-4 h-4" style={{ color: conf.color }} />
                <span className="text-xs font-semibold" style={{ color: conf.color }}>{conf.label}</span>
                <span className="text-[10px] text-slate-600 ml-auto">{stageTasks.length}</span>
              </div>
              <div className="space-y-2">
                {stageTasks.map(task => (
                  <motion.div key={task.id} layout className="flex items-center gap-2 px-3 py-2.5 bg-white/[0.02] border border-white/[0.04] rounded-lg hover:border-white/[0.08] transition-all group">
                    <button onClick={() => handleStageChange(task, nextStage(stage))}
                      className="flex-shrink-0 hover:scale-110 transition-transform" style={{ color: conf.color }}>
                      <conf.icon className="w-4 h-4" />
                    </button>
                    <span className={`text-xs flex-1 min-w-0 truncate ${stage === "done" ? "text-slate-500 line-through" : "text-slate-300"}`}>{task.title}</span>
                    <button onClick={() => handleDeleteTask(task)}
                      className="p-1 rounded text-slate-700 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </motion.div>
                ))}
                {stageTasks.length === 0 && <p className="text-[11px] text-slate-700 text-center py-4">Vazio</p>}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function CreateProjectModal({ isOpen, onClose, onSubmit }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { if (isOpen) { setTitle(""); setDescription(""); setEndDate(""); } }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault(); if (!title.trim()) return;
    setIsSubmitting(true);
    await onSubmit({ title, description, end_date: endDate || null });
    setIsSubmitting(false); onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()} className="bg-[#0d1220] border border-white/[0.08] rounded-2xl p-6 safe-modal-pb w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-5">Novo Projeto</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Nome</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Lançamento do App"
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors" autoFocus />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Descrição</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detalhes..." rows={2}
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors resize-none" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Prazo</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:border-cyan-500/30 transition-colors" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-3 rounded-xl border border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/5 transition-colors">Cancelar</button>
                <button type="submit" disabled={!title.trim() || isSubmitting}
                  className="flex-1 px-4 py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 disabled:opacity-40 transition-all flex items-center justify-center gap-2 font-medium">
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />} Criar
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function ProjectsSection() {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [view360ProjectId, setView360ProjectId] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/projects`);
      if (res.ok) { const data = await res.json(); setProjects(data.projects || []); }
    } catch (err) { console.error("Error:", err); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleCreate = async (data) => {
    try {
      const res = await authFetch(`${API_URL}/api/projects`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
      if (res.ok) { toast.success("Projeto criado!"); fetchProjects(); }
    } catch (err) { toast.error("Erro"); }
  };

  const handleDelete = async (project) => {
    if (!window.confirm("Excluir este projeto e todas as tarefas?")) return;
    try {
      const res = await authFetch(`${API_URL}/api/projects/${project.id}`, { method: "DELETE" });
      if (res.ok) { setProjects(prev => prev.filter(p => p.id !== project.id)); toast.success("Excluído"); }
    } catch (err) { console.error(err); }
  };

  const handleSelectProject = async (project) => {
    try {
      const res = await authFetch(`${API_URL}/api/projects/${project.id}`);
      if (res.ok) { const data = await res.json(); setSelectedProject(data); }
    } catch (err) { console.error(err); }
  };

  const stats = useMemo(() => {
    const total = projects.length;
    const totalTasks = projects.reduce((s, p) => s + (p.task_count || 0), 0);
    const doneTasks = projects.reduce((s, p) => s + (p.done_count || 0), 0);
    const completed = projects.filter(p => p.task_count > 0 && p.done_count === p.task_count).length;
    return { total, totalTasks, doneTasks, completed };
  }, [projects]);

  const overallProgress = stats.totalTasks > 0 ? (stats.doneTasks / stats.totalTasks) * 100 : 0;

  const chartData = useMemo(() => {
    const stages = { todo: 0, doing: 0, paused: 0, done: 0 };
    projects.forEach(p => {
      (p.tasks || []).forEach(t => { stages[t.stage || "todo"]++; });
    });
    return Object.entries(stages).filter(([, v]) => v > 0).map(([k, v]) => ({
      name: stageConfig[k]?.label || k, value: v, color: stageConfig[k]?.color || "#64748b",
    }));
  }, [projects]);

  if (selectedProject) {
    return (
      <div className="p-6 md:p-8 pb-32 max-w-6xl mx-auto" data-testid="projects-section">
        <ProjectDetail project={selectedProject} onBack={() => setSelectedProject(null)} onRefresh={fetchProjects} />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 pb-32 max-w-6xl mx-auto" data-testid="projects-section">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-wide">Projetos</h1>
          <p className="text-sm text-slate-500 mt-1">{stats.total} projeto{stats.total !== 1 ? "s" : ""} &middot; {stats.totalTasks} tarefas</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <NotificationBell />
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 transition-all text-sm font-medium" data-testid="create-project-btn">
            <Plus className="w-4 h-4" /> Novo Projeto
          </motion.button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatsCard icon={FolderKanban} value={stats.total} label="Projetos" color="#06b6d4" />
        <StatsCard icon={Target} value={stats.totalTasks} label="Tarefas" color="#64748b" />
        <StatsCard icon={CheckCircle2} value={stats.doneTasks} label="Concluídas" color="#22c55e" subtitle={stats.totalTasks > 0 ? `${Math.round(overallProgress)}% do total` : undefined} />
        <StatsCard icon={CheckSquare} value={stats.completed} label="Projetos completos" color="#a855f7" />

        {/* Mini Donut Chart */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#0d1220] border border-white/[0.06] rounded-2xl p-3 col-span-2 lg:col-span-1" data-testid="projects-chart">
          <p className="text-xs text-slate-500 mb-1">Tarefas</p>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={72}>
              <PieChart>
                <Pie data={chartData} cx="50%" cy="50%" innerRadius={20} outerRadius={32} dataKey="value" stroke="none" paddingAngle={2}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} tarefas`, n]} contentStyle={{ backgroundColor: "#0f1629", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", fontSize: "11px" }} itemStyle={{ color: "#e2e8f0" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-600 text-center mt-4">Sem dados</p>}
        </motion.div>
      </div>

      {/* Progress Bar */}
      {stats.totalTasks > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-slate-500">Progresso global</span>
            <span className="text-cyan-400 font-medium">{Math.round(overallProgress)}%</span>
          </div>
          <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${overallProgress}%` }}
              transition={{ duration: 1 }} className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full" />
          </div>
        </div>
      )}

      {/* Projects Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
      ) : projects.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
            <Target className="w-7 h-7 text-cyan-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-1.5">Nenhum projeto</h3>
          <p className="text-slate-600 text-sm mb-5">Organize suas metas em projetos</p>
          <button onClick={() => setIsModalOpen(true)} className="px-5 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm hover:bg-cyan-500/15 transition-all">Criar Projeto</button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence mode="popLayout">
            {projects.map(project => (
              <ProjectCard
                key={project.id}
                project={project}
                onDelete={handleDelete}
                onClick={handleSelectProject}
                onView360={(p) => setView360ProjectId(p.id)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      <CreateProjectModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleCreate} />

      <ProjectDetailPanel
        projectId={view360ProjectId}
        isOpen={!!view360ProjectId}
        onClose={() => setView360ProjectId(null)}
      />
    </div>
  );
}
